import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Paper,
  Typography,
  Stack,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  CircularProgress,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
} from '@mui/material'
import {
  CreditCard as CreditCardIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  History as HistoryIcon,
  SwapHoriz as SwapHorizIcon,
  Edit as EditIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'

interface BillingPlan {
  id: string
  planType: 'FREE' | 'BASIC' | 'CUSTOM'
  name: string
  nameJa: string
  basePrice: number
  pricePerUser: number
  maxUsers: number | null
  aiCreditsPerMonth: number | null
  aiCreditPrice: number
  creditCarryoverMonths: number
  reportRetentionYears: number
  maxLearningPlans: number | null
  features: Record<string, boolean>
  isActive: boolean
}

interface Subscription {
  id: string
  tenantId: string
  billingPlanId: string
  status: 'ACTIVE' | 'CANCELLED' | 'SUSPENDED'
  startDate: string
  endDate: string | null
  billingEmail: string | null
  billingName: string | null
  billingAddress: string | null
  billingPlan: BillingPlan
  activeUserCount: number
  previousPlanId: string | null
  planChangedAt: string | null
}

interface CreditBalance {
  id: string
  yearMonth: string
  creditsGranted: number
  creditsCarriedOver: number
  creditsUsed: number
  creditsExpired: number
  creditsRemaining: number
  creditsPurchased: number
  expiresAt: string | null
}

interface CreditUsage {
  id: string
  usageType: 'TEST_GENERATION' | 'CONTENT_TRANSLATION'
  creditsUsed: number
  usedAt: string
  usedByUser: {
    name: string
    email: string
  } | null
}

const SubscriptionPage: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { token } = useAuth()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [creditBalances, setCreditBalances] = useState<CreditBalance[]>([])
  const [creditUsages, setCreditUsages] = useState<CreditUsage[]>([])
  const [plans, setPlans] = useState<BillingPlan[]>([])

  const [planChangeDialogOpen, setPlanChangeDialogOpen] = useState(false)
  const [selectedPlanId, setSelectedPlanId] = useState<string>('')
  const [planChangeLoading, setPlanChangeLoading] = useState(false)

  // 請求先情報編集用state
  const [billingEditDialogOpen, setBillingEditDialogOpen] = useState(false)
  const [billingEmail, setBillingEmail] = useState('')
  const [billingName, setBillingName] = useState('')
  const [billingAddress, setBillingAddress] = useState('')
  const [billingEditLoading, setBillingEditLoading] = useState(false)
  const [billingEmailError, setBillingEmailError] = useState('')

  const isJapanese = i18n.language === 'ja'

  const fetchSubscription = useCallback(async () => {
    try {
      const response = await fetch('/api/billing/subscriptions/my', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.ok) {
        const result = await response.json()
        setSubscription(result.data || result)
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err)
    }
  }, [token])

  const fetchCreditBalance = useCallback(async () => {
    try {
      const response = await fetch('/api/billing/credits/balance', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.ok) {
        const result = await response.json()
        const data = result.data || result
        // 無制限プランの場合はisUnlimitedがtrue
        if (data && data.isUnlimited) {
          // 無制限の場合は空配列（後続の処理でisUnlimitedCreditsを参照）
          setCreditBalances([])
        } else if (data && data.yearMonth) {
          // APIは現在月の単一オブジェクトを返すので、配列に変換
          setCreditBalances([data])
        } else if (data.balances) {
          // 将来的に複数月を返す場合への対応
          setCreditBalances(data.balances)
        } else {
          setCreditBalances([])
        }
      }
    } catch (err) {
      console.error('Failed to fetch credit balance:', err)
    }
  }, [token])

  const fetchCreditUsage = useCallback(async () => {
    try {
      const response = await fetch('/api/billing/credits/usage?limit=20', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.ok) {
        const result = await response.json()
        const data = result.data || result
        setCreditUsages(data.usages || [])
      }
    } catch (err) {
      console.error('Failed to fetch credit usage:', err)
    }
  }, [token])

  const fetchPlans = useCallback(async () => {
    try {
      const response = await fetch('/api/billing/plans', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.ok) {
        const result = await response.json()
        const data = result.data || result
        const plansArray = Array.isArray(data) ? data : []
        setPlans(plansArray.filter((p: BillingPlan) => p.isActive && p.planType !== 'CUSTOM'))
      }
    } catch (err) {
      console.error('Failed to fetch plans:', err)
    }
  }, [token])

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      try {
        await Promise.all([
          fetchSubscription(),
          fetchCreditBalance(),
          fetchCreditUsage(),
          fetchPlans(),
        ])
      } catch (err) {
        setError(isJapanese ? 'データの読み込みに失敗しました' : 'Failed to load data')
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [fetchSubscription, fetchCreditBalance, fetchCreditUsage, fetchPlans, isJapanese])

  const handlePlanChange = async () => {
    if (!selectedPlanId || !subscription) return

    setPlanChangeLoading(true)
    try {
      const response = await fetch('/api/billing/subscriptions/my/change-plan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ newPlanId: selectedPlanId }),
      })

      if (response.ok) {
        setPlanChangeDialogOpen(false)
        await fetchSubscription()
      } else {
        const errorData = await response.json()
        setError(
          errorData.error || (isJapanese ? 'プラン変更に失敗しました' : 'Failed to change plan')
        )
      }
    } catch (err) {
      setError(isJapanese ? 'プラン変更に失敗しました' : 'Failed to change plan')
    } finally {
      setPlanChangeLoading(false)
    }
  }

  const openBillingEditDialog = () => {
    if (subscription) {
      setBillingEmail(subscription.billingEmail || '')
      setBillingName(subscription.billingName || '')
      setBillingAddress(subscription.billingAddress || '')
      setBillingEmailError('')
      setBillingEditDialogOpen(true)
    }
  }

  const validateBillingEmail = (email: string): boolean => {
    if (!email || email.trim() === '') {
      setBillingEmailError(
        isJapanese ? '請求先メールアドレスは必須です' : 'Billing email is required'
      )
      return false
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setBillingEmailError(
        isJapanese ? '有効なメールアドレスを入力してください' : 'Please enter a valid email address'
      )
      return false
    }
    setBillingEmailError('')
    return true
  }

  const handleBillingUpdate = async () => {
    if (!validateBillingEmail(billingEmail)) return

    setBillingEditLoading(true)
    try {
      const response = await fetch('/api/billing/subscriptions/my/billing-info', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          billingEmail: billingEmail.trim(),
          billingName: billingName.trim() || null,
          billingAddress: billingAddress.trim() || null,
        }),
      })

      if (response.ok) {
        setBillingEditDialogOpen(false)
        await fetchSubscription()
      } else {
        const errorData = await response.json()
        setError(
          errorData.error ||
            (isJapanese ? '請求先情報の更新に失敗しました' : 'Failed to update billing info')
        )
      }
    } catch (err) {
      setError(isJapanese ? '請求先情報の更新に失敗しました' : 'Failed to update billing info')
    } finally {
      setBillingEditLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(isJapanese ? 'ja-JP' : 'en-US')
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString(isJapanese ? 'ja-JP' : 'en-US')
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'primary'
      case 'CANCELLED':
        return 'error'
      case 'SUSPENDED':
        return 'secondary'
      default:
        return 'default'
    }
  }

  const getStatusLabel = (status: string) => {
    if (isJapanese) {
      switch (status) {
        case 'ACTIVE':
          return '有効'
        case 'CANCELLED':
          return 'キャンセル済み'
        case 'SUSPENDED':
          return '一時停止'
        default:
          return status
      }
    }
    return status
  }

  const getPlanTypeLabel = (planType: string) => {
    if (isJapanese) {
      switch (planType) {
        case 'FREE':
          return 'フリー'
        case 'BASIC':
          return 'ベーシック'
        case 'CUSTOM':
          return 'カスタム'
        default:
          return planType
      }
    }
    return planType
  }

  const getUsageTypeLabel = (usageType: string) => {
    if (isJapanese) {
      switch (usageType) {
        case 'TEST_GENERATION':
          return 'テスト生成'
        case 'CONTENT_TRANSLATION':
          return 'コンテンツ翻訳'
        default:
          return usageType
      }
    }
    switch (usageType) {
      case 'TEST_GENERATION':
        return 'Test Generation'
      case 'CONTENT_TRANSLATION':
        return 'Content Translation'
      default:
        return usageType
    }
  }

  // 今月のクレジット残高を計算
  const currentMonthBalance = creditBalances.find((b) => {
    const now = new Date()
    const yearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    return b.yearMonth === yearMonth
  })

  // 無制限プランかどうか
  const isUnlimitedCredits = subscription?.billingPlan?.aiCreditsPerMonth === null

  const totalCreditsAvailable = currentMonthBalance
    ? currentMonthBalance.creditsGranted +
      currentMonthBalance.creditsCarriedOver -
      currentMonthBalance.creditsExpired
    : isUnlimitedCredits
      ? null
      : subscription?.billingPlan?.aiCreditsPerMonth || 0

  const creditsUsed = currentMonthBalance?.creditsUsed || 0
  const creditsPurchased = currentMonthBalance?.creditsPurchased || 0
  const creditsRemaining = isUnlimitedCredits
    ? null // 無制限の場合はnull
    : currentMonthBalance?.creditsRemaining || (totalCreditsAvailable || 0) - creditsUsed
  const creditUsagePercent =
    totalCreditsAvailable && totalCreditsAvailable > 0
      ? Math.min((creditsUsed / totalCreditsAvailable) * 100, 100)
      : 0

  // 追加クレジット購入費用
  const creditPurchaseCost = creditsPurchased * (subscription?.billingPlan?.aiCreditPrice || 0)

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        {isJapanese ? '契約情報' : 'Subscription'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* 現在のプラン */}
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <CreditCardIcon color="primary" />
            <Typography variant="h6">{isJapanese ? '現在のプラン' : 'Current Plan'}</Typography>
          </Stack>

          {subscription && subscription.billingPlan ? (
            <Box>
              <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                <Typography variant="h5" fontWeight="bold">
                  {isJapanese ? subscription.billingPlan.nameJa : subscription.billingPlan.name}
                </Typography>
                <Chip
                  label={getPlanTypeLabel(subscription.billingPlan.planType)}
                  color="primary"
                  size="small"
                />
                <Chip
                  label={getStatusLabel(subscription.status)}
                  color={
                    getStatusColor(subscription.status) as
                      | 'primary'
                      | 'error'
                      | 'secondary'
                      | 'default'
                  }
                  size="small"
                />
              </Stack>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      {isJapanese ? '基本料金' : 'Base Price'}
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(subscription.billingPlan.basePrice)}/
                      {isJapanese ? '月' : 'month'}
                    </Typography>
                  </Stack>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      {isJapanese ? 'ユーザー単価' : 'Price per User'}
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(subscription.billingPlan.pricePerUser)}/
                      {isJapanese ? '人' : 'user'}
                    </Typography>
                  </Stack>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      {isJapanese ? 'アクティブユーザー数' : 'Active Users'}
                    </Typography>
                    <Typography variant="h6">
                      {subscription.activeUserCount}
                      {isJapanese ? '名' : ''}
                    </Typography>
                  </Stack>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      {isJapanese ? '今月の予想料金' : 'Estimated Monthly Cost'}
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      {formatCurrency(
                        subscription.billingPlan.basePrice +
                          subscription.billingPlan.pricePerUser * subscription.activeUserCount +
                          creditPurchaseCost
                      )}
                    </Typography>
                  </Stack>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {isJapanese ? '契約開始日' : 'Start Date'}
                  </Typography>
                  <Typography>{formatDate(subscription.startDate)}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {isJapanese ? 'AIクレジット' : 'AI Credits'}
                  </Typography>
                  <Typography>
                    {subscription.billingPlan.aiCreditsPerMonth === null
                      ? isJapanese
                        ? '無制限'
                        : 'Unlimited'
                      : `${subscription.billingPlan.aiCreditsPerMonth}${isJapanese ? '回/月' : '/month'}`}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {isJapanese ? 'データ保存期間' : 'Data Retention'}
                  </Typography>
                  <Typography>
                    {subscription.billingPlan.reportRetentionYears}
                    {isJapanese ? '年' : ' years'}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {isJapanese ? '学習計画' : 'Learning Plans'}
                  </Typography>
                  <Typography>
                    {subscription.billingPlan.maxLearningPlans === null
                      ? isJapanese
                        ? '無制限'
                        : 'Unlimited'
                      : `${subscription.billingPlan.maxLearningPlans}${isJapanese ? '件まで' : ''}`}
                  </Typography>
                </Box>
              </Box>

              {subscription.billingPlan.planType !== 'CUSTOM' && (
                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="outlined"
                    startIcon={<SwapHorizIcon />}
                    onClick={() => {
                      setSelectedPlanId('')
                      setPlanChangeDialogOpen(true)
                    }}
                  >
                    {isJapanese ? 'プランを変更' : 'Change Plan'}
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            <Alert severity="info">
              {isJapanese ? '契約情報がありません' : 'No subscription found'}
            </Alert>
          )}
        </Paper>

        {/* AIクレジット使用状況 */}
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <TrendingUpIcon color="primary" />
            <Typography variant="h6">
              {isJapanese ? 'AIクレジット使用状況' : 'AI Credit Usage'}
            </Typography>
          </Stack>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {isJapanese ? '今月の残高' : 'Remaining This Month'}
                </Typography>
                <Typography variant="h4" color="primary.main">
                  {isUnlimitedCredits
                    ? isJapanese
                      ? '無制限'
                      : 'Unlimited'
                    : Math.max(0, creditsRemaining ?? 0)}
                </Typography>
                {!isUnlimitedCredits && (
                  <Typography variant="caption" color="text.secondary">
                    / {totalCreditsAvailable} {isJapanese ? 'クレジット' : 'credits'}
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {isJapanese ? '今月の使用量' : 'Used This Month'}
                </Typography>
                <Typography variant="h4">{creditsUsed}</Typography>
                <Box sx={{ mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={creditUsagePercent}
                    color={creditUsagePercent > 80 ? 'secondary' : 'primary'}
                  />
                </Box>
              </CardContent>
            </Card>

            {subscription &&
              subscription.billingPlan &&
              subscription.billingPlan.creditCarryoverMonths > 0 && (
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {isJapanese ? '繰越クレジット' : 'Carried Over'}
                    </Typography>
                    <Typography variant="h4">
                      {currentMonthBalance?.creditsCarriedOver || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {isJapanese
                        ? `${subscription.billingPlan.creditCarryoverMonths}ヶ月繰越可能`
                        : `${subscription.billingPlan.creditCarryoverMonths} months carryover`}
                    </Typography>
                  </CardContent>
                </Card>
              )}
          </Box>
        </Paper>

        {/* クレジット使用履歴 */}
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <HistoryIcon color="primary" />
            <Typography variant="h6">
              {isJapanese ? 'クレジット使用履歴' : 'Credit Usage History'}
            </Typography>
          </Stack>

          {creditUsages.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{isJapanese ? '日時' : 'Date'}</TableCell>
                    <TableCell>{isJapanese ? '種類' : 'Type'}</TableCell>
                    <TableCell>{isJapanese ? '実行者' : 'User'}</TableCell>
                    <TableCell align="right">{isJapanese ? '使用量' : 'Credits Used'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {creditUsages.map((usage) => (
                    <TableRow key={usage.id}>
                      <TableCell>{formatDateTime(usage.usedAt)}</TableCell>
                      <TableCell>
                        <Chip
                          label={getUsageTypeLabel(usage.usageType)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{usage.usedByUser?.name || '-'}</TableCell>
                      <TableCell align="right">{usage.creditsUsed}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography color="text.secondary">
              {isJapanese ? '使用履歴がありません' : 'No usage history'}
            </Typography>
          )}
        </Paper>

        {/* 請求先情報 */}
        {subscription && (
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AccountBalanceIcon color="primary" />
                <Typography variant="h6">
                  {isJapanese ? '請求先情報' : 'Billing Information'}
                </Typography>
              </Stack>
              <IconButton onClick={openBillingEditDialog} size="small" color="primary">
                <EditIcon />
              </IconButton>
            </Stack>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {isJapanese ? '請求先メール' : 'Billing Email'}
                </Typography>
                <Typography>
                  {subscription.billingEmail || (isJapanese ? '未設定' : 'Not set')}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {isJapanese ? '請求先名' : 'Billing Name'}
                </Typography>
                <Typography>
                  {subscription.billingName || (isJapanese ? '未設定' : 'Not set')}
                </Typography>
              </Box>
              <Box sx={{ flex: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {isJapanese ? '請求先住所' : 'Billing Address'}
                </Typography>
                <Typography>
                  {subscription.billingAddress || (isJapanese ? '未設定' : 'Not set')}
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}
      </Stack>

      {/* プラン変更ダイアログ */}
      <Dialog
        open={planChangeDialogOpen}
        onClose={() => setPlanChangeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{isJapanese ? 'プラン変更' : 'Change Plan'}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {isJapanese
              ? 'プラン変更は即時適用され、日割り計算で請求されます。'
              : 'Plan changes take effect immediately and are prorated.'}
          </Alert>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{isJapanese ? '新しいプラン' : 'New Plan'}</InputLabel>
            <Select
              value={selectedPlanId}
              onChange={(e) => setSelectedPlanId(e.target.value)}
              label={isJapanese ? '新しいプラン' : 'New Plan'}
            >
              {plans
                .filter((p) => p.id !== subscription?.billingPlanId)
                .map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {isJapanese ? plan.nameJa : plan.name} - {formatCurrency(plan.basePrice)}/月
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanChangeDialogOpen(false)}>{t('buttons.cancel')}</Button>
          <Button
            onClick={handlePlanChange}
            variant="contained"
            disabled={!selectedPlanId || planChangeLoading}
          >
            {planChangeLoading ? (
              <CircularProgress size={20} />
            ) : isJapanese ? (
              '変更する'
            ) : (
              'Change'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 請求先情報編集ダイアログ */}
      <Dialog
        open={billingEditDialogOpen}
        onClose={() => setBillingEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{isJapanese ? '請求先情報の編集' : 'Edit Billing Information'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label={isJapanese ? '請求先メールアドレス' : 'Billing Email'}
              value={billingEmail}
              onChange={(e) => {
                setBillingEmail(e.target.value)
                if (billingEmailError) validateBillingEmail(e.target.value)
              }}
              onBlur={() => validateBillingEmail(billingEmail)}
              error={!!billingEmailError}
              helperText={billingEmailError || (isJapanese ? '必須項目です' : 'Required')}
              fullWidth
              required
              type="email"
            />
            <TextField
              label={isJapanese ? '請求先名' : 'Billing Name'}
              value={billingName}
              onChange={(e) => setBillingName(e.target.value)}
              fullWidth
              helperText={isJapanese ? '会社名など' : 'Company name, etc.'}
            />
            <TextField
              label={isJapanese ? '請求先住所' : 'Billing Address'}
              value={billingAddress}
              onChange={(e) => setBillingAddress(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBillingEditDialogOpen(false)}>{t('buttons.cancel')}</Button>
          <Button
            onClick={handleBillingUpdate}
            variant="contained"
            disabled={billingEditLoading || !billingEmail.trim()}
          >
            {billingEditLoading ? <CircularProgress size={20} /> : isJapanese ? '保存' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default SubscriptionPage
